import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { Progress } from "../components/ui/progress";
import { Trophy, Gift, Check } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { sfx } from "../lib/sound";
import LoadingState from "../components/LoadingState";
import { useTranslation } from "react-i18next";

export default function Challenges() {
  const { t } = useTranslation();
  const { setProfile } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { data } = await api.get(`/challenges?local_hour=${new Date().getHours()}`);
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || t("challenges.couldnt_load"));
    }
  }, [t]);
  useEffect(() => { load(); }, [load]);

  const claim = async (id) => {
    try {
      const { data } = await api.post(`/challenges/claim/${id}?local_hour=${new Date().getHours()}`);
      setProfile(data.profile);
      sfx.coinCash();
      toast.success(t("challenges.toast_claimed", { n: data.reward_coins }));
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || t("challenges.toast_cannot_claim"));
    }
  };

  if (!data || error) return <LoadingState error={error} onRetry={load} rows={4} testid="challenges" />;

  return (
    <div className="space-y-8">
      <div className="text-center">
        <p className="text-xs uppercase tracking-[0.25em] text-[#8AA48F] mb-1.5">{t("challenges.this_week", { week: data.week })}</p>
        <h1 style={{ fontFamily: "Outfit, sans-serif" }} className="text-2xl sm:text-3xl font-light tracking-tight">
          {t("challenges.headline")}
        </h1>
        <p className="text-sm text-[#2C332A]/70 mt-2 max-w-xl mx-auto">
          {t("challenges.intro")}
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {data.challenges.map((c) => {
          const pct = Math.min(100, Math.round((c.progress / c.target) * 100));
          const complete = c.progress >= c.target;
          const remaining = c.target - c.progress;
          return (
            <div
              key={c.challenge_id}
              data-testid={`challenge-${c.challenge_id}`}
              className="p-8 rounded-3xl bg-white border border-[#E6E2D8]"
            >
              <div className="flex items-start justify-between mb-4">
                {c.claimed
                  ? <Check className="text-[#5F7161]" strokeWidth={2} />
                  : <Trophy className={complete ? "text-[#5F7161]" : "text-[#D0A98F]"} strokeWidth={1.5} />}
                <span className="text-sm text-[#8AA48F] flex items-center gap-1">
                  <Gift size={14} strokeWidth={1.5} /> {t("challenges.coins_reward", { n: c.reward_coins })}
                </span>
              </div>
              <h3 style={{ fontFamily: "Outfit, sans-serif" }} className="text-xl font-medium mb-2">
                {t(`challenges.title_${c.challenge_id}`, c.title)}
              </h3>
              <p className="text-sm text-[#2C332A]/60 mb-4">
                {c.claimed
                  ? t("challenges.reward_claimed")
                  : complete
                    ? t("challenges.ready_claim")
                    : c.progress === 0
                      ? t("challenges.start_playing")
                      : remaining === 1
                        ? t("challenges.n_steps_away", { n: remaining })
                        : t("challenges.n_steps_away_plural", { n: remaining })}
              </p>
              <Progress
                value={pct}
                className={`h-2 mb-2 ${c.claimed ? "bg-[#E6E2D8] opacity-50 [&>div]:bg-[#A8B5A8]" : "bg-[#E6E2D8]"}`}
              />
              <p className="text-xs text-[#2C332A]/50 mb-4">
                {c.progress} / {c.target}
              </p>
              <Button
                data-testid={`claim-${c.challenge_id}`}
                disabled={!complete || c.claimed}
                onClick={() => claim(c.challenge_id)}
                className="rounded-full bg-[#5F7161] hover:bg-[#4D5D4F] text-white disabled:bg-[#E6E2D8] disabled:text-[#6E756B] disabled:opacity-100"
              >
                {c.claimed ? t("challenges.btn_claimed") : complete ? t("challenges.btn_claim") : t("challenges.btn_in_progress")}
              </Button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
